/* Inventory — current stock by SKU / batch / warehouse with FIFO
 * expiry awareness. Tabs: In stock · Expiring · Low stock · Movements.
 * Data comes through supplyService (API placeholder layer). */
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import supplyService from "../../Services/supplyService";
import rackService from "./Racks/rackService";
import { RackPicker } from "./Racks/RackPicker";
import inventoryReportService from "./Reports/inventoryReportService";
import { cbFmtINR, cbFmtNum, cbExpiryStatus } from "./supplyData";
import { fmtDateTime as fmtDateTime12 } from "../../Utils/dateFormat";
import { useWarehouse } from "../Layout/WarehouseContext";
import { useTaskCenter } from "../Layout/TaskCenterContext";
import {
  Button,
  Pill,
  Input,
  Select,
  Table,
  Tr,
  Td,
  SectionHeader,
  ToolbarBar,
  DateRangeFilter,
  TabBar,
  KpiBox,
  LoadingState,
  ErrorState,
  IconDownload,
  IconPlus,
  IconFilter,
  IconChevRight,
} from "./supplyKit";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const POLL_INTERVAL = 1500;
const POLL_MAX_ATTEMPTS = 40; // ~60s before timing out

const Inventory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { runTask } = useTaskCenter();
  /* When opened via Grn's "View ledger" button we receive { tab, refId } so the
     page lands on the Movements tab pre-filtered by that GRN's purchase id. */
  const [tab, setTab] = useState(location.state?.tab || "stock");
  const [refIdFilter, setRefIdFilter] = useState(location.state?.refId || null);
  /* The store being looked at comes from the topbar switcher — one selection for
     the whole app. Empty only until a head has picked one; the table says so
     rather than showing one store's stock as if it were the answer. */
  const { code: wh, isAll } = useWarehouse();
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  /* Debounced copy of `query` sent to the API as the optional `search` param. */
  const [searchTerm, setSearchTerm] = useState("");
  const [medFilter, setMedFilter] = useState("");
  const [skuFilter, setSkuFilter] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [lots, setLots] = useState([]);
  /* Server-side pagination for the warehouse stock fetch. */
  const [stock, setStock] = useState({ page: 0, size: 20, totalPages: 0, totalElements: 0 });
  const setStockField = (key, val) =>
    setStock((s) => ({ ...s, [key]: val, ...(key === "size" ? { page: 0 } : {}) }));
  const [medicines, setMedicines] = useState([]);
  const [movements, setMovements] = useState([]);
  const [ledger, setLedger] = useState(() => {
    return {
      /* Dates are an optional filter — empty by default so all movements show (newest first). */
      from: "",
      to: "",
      page: 0,
      size: 50,
      sortDir: "desc",
      totalPages: 0,
      totalElements: 0,
      loading: false,
      error: null,
    };
  });
  const setLedgerField = (key, val) =>
    setLedger((l) => ({
      ...l,
      [key]: val,
      ...(key === "from" || key === "to" || key === "size" || key === "sortDir" ? { page: 0 } : {}),
    }));

  /* Static reference data — loaded once per reload. */
  useEffect(() => {
    let alive = true;
    supplyService
      .getMedicines()
      .then((m) => {
        if (!alive) return;
        setMedicines(m || []);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || "Could not load reference data.");
      });
    return () => {
      alive = false;
    };
  }, [reloadKey]);

  /* Stock-movement ledger — refetches on date / page / size change. */
  useEffect(() => {
    let alive = true;
    setLedger((l) => ({ ...l, loading: true, error: null }));
    supplyService
      .getStockMovements({
        warehouseId: wh,
        from: ledger.from,
        to: ledger.to,
        page: ledger.page,
        size: ledger.size,
        sort: `transactionDate,${ledger.sortDir}`,
      })
      .then((res) => {
        if (!alive) return;
        setMovements(res.content || []);
        setLedger((l) => ({
          ...l,
          totalPages: res.totalPages || 0,
          totalElements: res.totalElements || 0,
          loading: false,
        }));
      })
      .catch((e) => {
        if (!alive) return;
        setMovements([]);
        setLedger((l) => ({
          ...l,
          loading: false,
          totalPages: 0,
          totalElements: 0,
          error: e?.message || "Could not load stock movements.",
        }));
      });
    return () => {
      alive = false;
    };
  }, [wh, ledger.from, ledger.to, ledger.page, ledger.size, ledger.sortDir, reloadKey]);

  /* Resolving an unset warehouse is the provider's job now (WarehouseContext), so
     no screen repeats it. */

  /* Debounce the search box into `searchTerm` so we don't fire a request per keystroke. */
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(query.trim()), 400);
    return () => clearTimeout(t);
  }, [query]);

  /* A new search (or page-size change) always restarts from the first page. */
  useEffect(() => {
    setStock((s) => (s.page === 0 ? s : { ...s, page: 0 }));
  }, [searchTerm, wh]);

  /* Inventory lots — refetches when warehouse, search, or page/size changes. */
  useEffect(() => {
    if (!wh) {
      setLots([]);
      setStock((s) => ({ ...s, totalPages: 0, totalElements: 0 }));
      setLoading(false);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    supplyService
      .getInventoryLots(wh, { page: stock.page, size: stock.size, search: searchTerm })
      .then((res) => {
        if (!alive) return;
        setLots(res.content || []);
        setStock((s) => ({
          ...s,
          totalPages: res.totalPages || 0,
          totalElements: res.totalElements || 0,
        }));
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || "Could not load inventory.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [wh, searchTerm, stock.page, stock.size, reloadKey]);

  /* ---------------- Racks ----------------
     The store's racks, so a batch's location can be corrected from the row that
     shows it. Per warehouse, so switching stores reloads the list.

     Editing is at the BATCH level, because that is what a location is: a box on a
     shelf, not a medicine. The medicine-level home shelf — what prefills a GRN
     line — is set on Master › Racks instead. Where the two disagree the batch row
     says so, since the batch is the thing you can walk to. */
  const [racks, setRacks] = useState([]);

  useEffect(() => {
    if (!wh) {
      setRacks([]);
      return undefined;
    }
    let alive = true;
    rackService
      .listAll({ warehouseId: wh })
      .then((list) => alive && setRacks(list || []))
      .catch(() => alive && setRacks([]));
    return () => {
      alive = false;
    };
  }, [wh, reloadKey]);

  /* The rack the operator is standing at may not be in the master yet — creating
     it from the picker beats sending them to another screen mid-count. */
  const createRack = (details) =>
    rackService.create({ warehouseId: wh, ...details }).then((rack) => {
      setRacks((prev) => [...prev, rack].sort((a, b) => a.code.localeCompare(b.code)));
      toast.success(`Rack ${rack.code} created in ${wh}.`);
      return rack;
    });

  /* Put one batch in a rack, or take it out. Patches just that lot from the
     response instead of refetching the page — nothing else on screen changed, and
     a reload would collapse the expanded medicine the operator is working in. */
  const moveLot = (lot, rackId) =>
    rackService
      .moveLot({ inventoryId: lot.id, rackId })
      .then((saved) => {
        setLots((prev) =>
          prev.map((l) =>
            l.id === lot.id
              ? { ...l, rackId: saved.rackId, rackCode: saved.rackCode, rackSource: saved.rackSource }
              : l
          )
        );
        toast.success(
          rackId
            ? `${lot.medicineName || lot.medId} batch ${lot.batch} → rack ${saved.rackCode}.`
            : `${lot.medicineName || lot.medId} batch ${lot.batch} removed from its rack.`
        );
      })
      .catch((e) =>
        toast.error(e?.response?.data?.message || e?.message || "Could not move the batch.")
      );

  const medById = (id) => medicines.find((m) => m.id === id);
  /* Fallback to fields embedded on the lot when the medicines master is unavailable. */
  const medFor = (l) =>
    medById(l.medId) || { id: l.medId, name: l.medicineName, sku: l.medId, pack: "" };

  const q = query.trim();
  /* Text search is handled server-side via the `search` param; these are client-side
     refinements on the current page. Warehouse is enforced server-side too. */
  const lotVisible = (l) => {
    if (medFilter && l.medId !== medFilter) return false;
    if (skuFilter && medFor(l)?.sku !== skuFilter) return false;
    return true;
  };

  /* Aggregate lots by medicine for the "In stock" tab. */
  const byMed = {};
  lots.forEach((l) => {
    if (!lotVisible(l)) return;
    const med = medFor(l);
    if (!byMed[l.medId]) byMed[l.medId] = { med, lots: [], total: 0, value: 0 };
    byMed[l.medId].lots.push(l);
    byMed[l.medId].total += l.qtyA;
    byMed[l.medId].value += l.qtyA * l.mrp;
  });
  const stockRows = Object.values(byMed).sort((a, b) => b.total - a.total);

  const expiring = lots
    .filter((l) => lotVisible(l) && cbExpiryStatus(l.exp).days < 90)
    .sort((a, b) => a.exp.localeCompare(b.exp));

  const lowStock = lots.filter((l) => lotVisible(l) && l.qtyA < 100);

  /* Export = the whole in-stock book as CSV, not just the page on screen.
     Generation is async (task → poll → download) and runs through the global
     task center, so it survives navigating away and reports progress in the
     topbar bell. The active search box is passed through — what you are looking
     at is what you get — while the client-side medicine/SKU refinements are not,
     since the backend filter has no equivalent.

     Scope follows the topbar. On "All stores" the warehouseId is left out and the
     report covers every store; that scope is head-only by construction, since
     isAllStores() requires isPharmacyHead(). So the only blocking case left is a
     user with no store resolved at all. */
  const [exporting, setExporting] = useState(false);
  const startExport = () => {
    if (exporting) return;
    if (!wh && !isAll) {
      toast.info("Pick a store before exporting — stock only exists per store.");
      return;
    }
    setExporting(true);
    const req = { warehouseId: isAll ? "" : wh, search: searchTerm };
    runTask({
      title: isAll ? "In-Stock Inventory · All stores · CSV" : "In-Stock Inventory · CSV",
      run: async (onProgress) => {
        onProgress("Requesting…");
        const task = await inventoryReportService.requestExport(req);
        if (!task?.taskId) throw new Error("Export did not return a task id.");
        const fileName = task.fileName || `in-stock-inventory-${isAll ? "all-stores" : wh}.csv`;
        const doDownload = () => inventoryReportService.download(task.taskId, fileName);

        let cur = task;
        for (let i = 0; cur.status === "PENDING" && i < POLL_MAX_ATTEMPTS; i++) {
          onProgress("Generating report…");
          await sleep(POLL_INTERVAL);
          cur = await inventoryReportService.exportStatus(task.taskId);
        }
        if (cur.status === "FAILED") throw new Error(cur.message || "Report generation failed.");
        if (cur.status !== "READY") throw new Error("Report generation timed out. Please try again.");

        onProgress("Downloading…");
        await doDownload();
        return {
          detail: `Downloaded${cur.rowCount != null ? ` · ${cbFmtNum(cur.rowCount)} rows` : ""}`,
          action: { label: "Download again", run: doDownload },
        };
      },
    });
    toast.info("Generating report — track progress in notifications.");
    // brief guard against double-submit; the task itself lives in the task center
    setTimeout(() => setExporting(false), 1200);
  };

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionHeader
        title="Inventory"
        /* The store is named in the topbar, so this says what the screen is, not
           where — repeating the store name here would just disagree with it. */
        sub="On-hand stock by SKU and batch · FIFO by expiry, then inward date"
        actions={
          <>
            <Button icon={<IconFilter size={14} />}>Filters</Button>
            <Button
              icon={<IconDownload size={14} />}
              onClick={startExport}
              disabled={exporting || (!wh && !isAll)}
            >
              Export
            </Button>
            <Button
              icon={<IconDownload size={14} style={{ transform: "rotate(180deg)" }} />}
              onClick={() => navigate("/grn", { state: { mode: "upload" } })}
            >
              Upload GRN
            </Button>
            <Button
              variant="primary"
              icon={<IconPlus size={14} />}
              onClick={() => navigate("/grn", { state: { mode: "new" } })}
            >
              New GRN
            </Button>
          </>
        }
      />

      <TabBar
        value={tab}
        onChange={setTab}
        tabs={[
          { id: "stock", label: "In stock", count: stockRows.length },
          { id: "expiring", label: "Expiring ≤ 90d", count: expiring.length, alert: "amber" },
          { id: "low", label: "Low stock", count: lowStock.length, alert: "red" },
          { id: "movements", label: "Movements", count: ledger.totalElements || movements.length },
        ]}
      />

      <ToolbarBar>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search medicine, SKU, or batch…"
          style={{ width: 280, maxWidth: "100%" }}
        />
        <Select value={medFilter} onChange={(e) => setMedFilter(e.target.value)}>
          <option value="">All medicines</option>
          {medicines.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </Select>
        <Select value={skuFilter} onChange={(e) => setSkuFilter(e.target.value)}>
          <option value="">All SKUs</option>
          {medicines.map((m) => (
            <option key={m.id} value={m.sku}>
              {m.sku}
            </option>
          ))}
        </Select>
        {(query || medFilter || skuFilter) && (
          <button
            onClick={() => {
              setQuery("");
              setMedFilter("");
              setSkuFilter("");
            }}
            style={{
              border: 0,
              background: "transparent",
              color: "var(--fg-link)",
              cursor: "pointer",
              fontSize: 11,
              fontWeight: 600,
              padding: "0 4px",
              height: 28,
            }}
          >
            Clear filters
          </button>
        )}
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {q && <span style={{ color: "var(--ink-700)", marginRight: 8 }}>filter: “{query}” · </span>}
          {cbFmtNum(stock.totalElements)} SKUs
        </div>
      </ToolbarBar>

      <div className="grid-cols-1 sm:grid-cols-3" style={{ display: "grid", gap: 10 }}>
        <KpiBox label="SKUs in stock" value={cbFmtNum(stock.totalElements)} variant="ink" />
        <KpiBox label="Expiring ≤ 90d" value={expiring.length} variant="amber" />
        <KpiBox label="Low stock SKUs" value={lowStock.length} variant="red" />
      </div>

      {loading ? (
        <LoadingState label="Loading inventory…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : (
        <>
          {tab === "stock" && (
            <InventoryStock
              rows={stockRows}
              selected={selected}
              onSelect={setSelected}
              /* Stock is fetched per warehouse, so with none picked the table is
                 empty by design — say that instead of "no medicines match". */
              noWarehouse={!wh}
              allStores={isAll}
              racks={racks}
              onMoveLot={moveLot}
              onCreateRack={createRack}
            />
          )}
          {tab === "expiring" && (
            <InventoryExpiring
              lots={expiring}
              medFor={medFor}
              racks={racks}
              onMoveLot={moveLot}
              onCreateRack={createRack}
            />
          )}
          {tab === "low" && (
            <InventoryLow
              lots={lowStock}
              medFor={medFor}
              racks={racks}
              onMoveLot={moveLot}
              onCreateRack={createRack}
            />
          )}
          {tab !== "movements" && (
            <StockPagination stock={stock} setStock={setStockField} loading={loading} />
          )}
          {tab === "movements" && (
            <InventoryMovements
              movements={movements}
              ledger={ledger}
              setLedger={setLedgerField}
              refIdFilter={refIdFilter}
              clearRefFilter={() => setRefIdFilter(null)}
            />
          )}
        </>
      )}
    </div>
  );
};

/* ---------------- Pagination (stock / expiring / low tabs) ---------------- */
const StockPagination = ({ stock, setStock, loading }) => {
  const start = stock.totalElements === 0 ? 0 : stock.page * stock.size + 1;
  const end = Math.min(stock.totalElements, (stock.page + 1) * stock.size);
  return (
    <ToolbarBar>
      <Select value={stock.size} onChange={(e) => setStock("size", Number(e.target.value))}>
        {[20, 50, 100, 200].map((n) => (
          <option key={n} value={n}>
            {n} / page
          </option>
        ))}
      </Select>
      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {start}–{end} of {cbFmtNum(stock.totalElements)}
        </span>
        <Button
          size="sm"
          disabled={stock.page <= 0 || loading}
          onClick={() => setStock("page", Math.max(0, stock.page - 1))}
        >
          ← Prev
        </Button>
        <Button
          size="sm"
          disabled={stock.page + 1 >= stock.totalPages || loading}
          onClick={() => setStock("page", stock.page + 1)}
        >
          Next →
        </Button>
      </div>
    </ToolbarBar>
  );
};

/* ---------------- One batch's rack, editable ----------------
   The distinction the cell has to carry: a PLACED rack was recorded against this
   batch and is a fact; a HOME rack is the backend saying "nobody put this batch
   away, but its medicine belongs here" — a good place to look, not a location. So
   PLACED renders solid and HOME renders muted italic, and picking a rack records
   it against THIS batch either way, which is also how a HOME guess gets confirmed
   into a fact. */
const LotRackCell = ({ lot, racks, onMoveLot, onCreateRack, disabled, warehouseLabel, width = 104 }) => {
  const [busy, setBusy] = useState(false);
  const placed = lot.rackSource === "PLACED";
  const move = (rackId) => {
    setBusy(true);
    Promise.resolve(onMoveLot(lot, rackId)).finally(() => setBusy(false));
  };
  return (
    /* The row's own click expands / selects it; opening the picker must not. */
    <Td onClick={(e) => e.stopPropagation()}>
      <RackPicker
        value={placed ? lot.rackId : null}
        racks={racks || []}
        fallbackLabel={placed ? lot.rackCode : ""}
        /* Not placed: the home shelf shows through as the placeholder, so it reads
           as a suggestion the operator has not confirmed. */
        placeholder={lot.rackCode || "— set —"}
        tone={placed ? "default" : "muted"}
        width={width}
        height={26}
        disabled={disabled || busy}
        disabledHint={disabled ? "Pick a warehouse first" : "Saving…"}
        warehouseLabel={warehouseLabel || lot.wh}
        title={
          placed
            ? `Batch ${lot.batch} is in rack ${lot.rackCode} · pick another to move it`
            : lot.rackCode
            ? `Batch ${lot.batch} has not been put away — ${lot.medicineName || lot.medId} belongs on ${lot.rackCode}. Pick a rack to record where this batch actually is.`
            : `Batch ${lot.batch} has no location · pick a rack to record where it is`
        }
        onChange={(rackId) => move(rackId)}
        onCreate={onCreateRack}
      />
    </Td>
  );
};

/* ---------------- In stock ---------------- */
const InventoryStock = ({
  rows,
  selected,
  onSelect,
  noWarehouse,
  allStores,
  racks,
  onMoveLot,
  onCreateRack,
}) => (
  <Table
    columns={[
      { label: "", width: 24 },
      { label: "Medicine" },
      { label: "SKU" },
      { label: "Batches", align: "right" },
      { label: "Total units", align: "right" },
      { label: "Earliest expiry" },
      { label: "Rack" },
      { label: "Warehouses" },
      { label: "Value at MRP", align: "right" },
      { label: "Status" },
    ]}
  >
    {rows.length === 0 && (
      <tr>
        <Td colSpan={10} style={{ textAlign: "center", color: "var(--fg-muted)", height: 80 }}>
          {allStores
            ? "Stock is held per store — pick a single store in the topbar to browse it, or Export for every store as CSV."
            : noWarehouse
            ? "Pick a warehouse to see its stock."
            : "No medicines match this filter."}
        </Td>
      </tr>
    )}
    {rows.map((r) => {
      const isOpen = selected === r.med?.id;
      const earliest = r.lots.map((l) => l.exp).sort()[0];
      const exp = cbExpiryStatus(earliest);
      const whs = [...new Set(r.lots.map((l) => l.wh))];
      /* A medicine's batches can be spread across racks, so the summary row lists
         what is actually holding stock rather than implying one location. Expand
         the row to change a batch's rack. */
      const lotRacks = [...new Set(r.lots.map((l) => l.rackCode).filter(Boolean))];
      return (
        <React.Fragment key={r.med?.id}>
          <Tr selected={isOpen} onClick={() => onSelect(isOpen ? null : r.med?.id)}>
            <Td>
              <IconChevRight
                size={12}
                style={{
                  transform: isOpen ? "rotate(90deg)" : "rotate(0)",
                  transition: "transform 140ms",
                  color: "var(--fg-muted)",
                }}
              />
            </Td>
            <Td>
              <span style={{ fontWeight: 600, color: "var(--fg-strong)" }}>{r.med?.name}</span>
              <span style={{ marginLeft: 8, fontSize: 11, color: "var(--fg-muted)" }}>{r.med?.pack}</span>
            </Td>
            <Td mono>{r.med?.sku}</Td>
            <Td mono right>
              {r.lots.length}
            </Td>
            <Td mono right style={{ fontWeight: 600, fontSize: 13 }}>
              {cbFmtNum(r.total)}
            </Td>
            <Td mono>{earliest}</Td>
            <Td mono title={lotRacks.join(", ")}>
              {lotRacks.length === 0 ? "—" : lotRacks.length <= 2 ? lotRacks.join(", ") : `${lotRacks.length} racks`}
            </Td>
            <Td>
              <div style={{ display: "flex", gap: 4 }}>
                {whs.map((w) => (
                  <Pill key={w} variant="neutral">
                    {w}
                  </Pill>
                ))}
              </div>
            </Td>
            <Td mono right>
              {cbFmtINR(r.value)}
            </Td>
            <Td>
              <Pill variant={exp.status} dot>
                {exp.label}
              </Pill>
            </Td>
          </Tr>
          {isOpen &&
            r.lots.map((l) => (
              <Tr key={l.id} style={{ background: "var(--bg-sunken)" }}>
                <Td />
                <Td
                  colSpan={2}
                  style={{ paddingLeft: 32, color: "var(--fg-muted)", fontFamily: "var(--font-mono)", fontSize: 11 }}
                >
                  Batch {l.id} · Batch <strong>{l.batch}</strong> · {l.grn}
                </Td>
                <Td mono right style={{ color: "var(--fg-muted)" }}>
                  {cbFmtINR(l.purchase)}
                </Td>
                <Td mono right>
                  {cbFmtNum(l.qtyA)}
                  <span style={{ color: "var(--fg-subtle)" }}> / {cbFmtNum(l.qtyR)}</span>
                </Td>
                <Td mono>{l.exp}</Td>
                <LotRackCell
                  lot={l}
                  racks={racks}
                  onMoveLot={onMoveLot}
                  onCreateRack={onCreateRack}
                  disabled={noWarehouse}
                />
                <Td>
                  <Pill variant="neutral">{l.wh}</Pill>
                </Td>
                <Td mono right>
                  {cbFmtINR(l.qtyA * l.mrp)}
                </Td>
                <Td>
                  <Pill variant={cbExpiryStatus(l.exp).status} dot>
                    {cbExpiryStatus(l.exp).label}
                  </Pill>
                </Td>
              </Tr>
            ))}
        </React.Fragment>
      );
    })}
  </Table>
);

/* ---------------- Expiring ---------------- */
const InventoryExpiring = ({ lots, medFor, racks, onMoveLot, onCreateRack }) => (
  <Table
    columns={[
      { label: "Medicine" },
      { label: "Batch" },
      { label: "Warehouse" },
      { label: "Batch" },
      { label: "Expiry" },
      { label: "Rack" },
      { label: "Days", align: "right" },
      { label: "Qty", align: "right" },
      { label: "Value at MRP", align: "right" },
      { label: "Action" },
    ]}
  >
    {lots.map((l) => {
      const med = medFor(l);
      const s = cbExpiryStatus(l.exp);
      return (
        <Tr key={l.id}>
          <Td>{med?.name}</Td>
          <Td mono>{l.id}</Td>
          <Td>
            <Pill variant="neutral">{l.wh}</Pill>
          </Td>
          <Td mono>{l.batch}</Td>
          <Td mono>{l.exp}</Td>
          {/* Editable here too: pulling short-dated stock is exactly when someone
              notices a batch is not where the system says it is. */}
          <LotRackCell lot={l} racks={racks} onMoveLot={onMoveLot} onCreateRack={onCreateRack} />
          <Td mono right style={{ color: s.days < 0 ? "var(--red-700)" : "var(--amber-700)", fontWeight: 600 }}>
            {s.days < 0 ? `${Math.abs(s.days)}d ago` : `${s.days}d`}
          </Td>
          <Td mono right>
            {cbFmtNum(l.qtyA)}
          </Td>
          <Td mono right>
            {cbFmtINR(l.qtyA * l.mrp)}
          </Td>
          <Td>
            {s.status === "expired" ? (
              <Button size="sm" variant="danger">
                Dispose
              </Button>
            ) : (
              <Button size="sm">Mark for FIFO</Button>
            )}
          </Td>
        </Tr>
      );
    })}
  </Table>
);

/* ---------------- Low stock ---------------- */
const InventoryLow = ({ lots, medFor, racks, onMoveLot, onCreateRack }) => (
  <Table
    columns={[
      { label: "Medicine" },
      { label: "Batch" },
      { label: "Warehouse" },
      { label: "Rack" },
      { label: "Received", align: "right" },
      { label: "Available", align: "right" },
      { label: "% left", align: "right" },
      { label: "Suggestion" },
    ]}
  >
    {lots.map((l) => {
      const med = medFor(l);
      const pct = Math.round((l.qtyA / l.qtyR) * 100);
      return (
        <Tr key={l.id}>
          <Td>{med?.name}</Td>
          <Td mono>{l.id}</Td>
          <Td>
            <Pill variant="neutral">{l.wh}</Pill>
          </Td>
          <LotRackCell lot={l} racks={racks} onMoveLot={onMoveLot} onCreateRack={onCreateRack} />
          <Td mono right>
            {cbFmtNum(l.qtyR)}
          </Td>
          <Td mono right style={{ color: pct < 30 ? "var(--red-700)" : "var(--amber-700)", fontWeight: 600 }}>
            {cbFmtNum(l.qtyA)}
          </Td>
          <Td mono right>
            {pct}%
          </Td>
          <Td>
            <Button size="sm" variant="primary" icon={<IconDownload size={12} style={{ transform: "rotate(180deg)" }} />}>
              Reorder
            </Button>
          </Td>
        </Tr>
      );
    })}
  </Table>
);

/* ---------------- Movements ---------------- */
/* "2026-06-19T13:07:48" -> "19 Jun 26, 01:07:48 PM". */
const fmtLedgerTime = (iso) => fmtDateTime12(iso);

const InventoryMovements = ({ movements, ledger, setLedger, refIdFilter, clearRefFilter }) => {
  /* When a GRN row sends us here via "View ledger", filter client-side by referenceId
     until the backend ledger endpoint supports `?referenceId=` directly. */
  const visible = refIdFilter
    ? movements.filter((m) => String(m.refId) === String(refIdFilter))
    : movements;
  const start = ledger.totalElements === 0 ? 0 : ledger.page * ledger.size + 1;
  const end = Math.min(ledger.totalElements, (ledger.page + 1) * ledger.size);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {refIdFilter && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 8,
            padding: "8px 12px",
            background: "var(--ink-50)",
            border: "1px solid var(--border-default)",
            borderRadius: 4,
            fontSize: 12,
          }}
        >
          <span>
            Showing movements for GRN <strong>#{refIdFilter}</strong> only.{" "}
            <span style={{ color: "var(--fg-muted)", fontSize: 11 }}>
              ({visible.length} of {movements.length} loaded on this page)
            </span>
          </span>
          <button
            onClick={clearRefFilter}
            style={{
              border: 0,
              background: "transparent",
              color: "var(--fg-link)",
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Clear filter
          </button>
        </div>
      )}
      <ToolbarBar>
        <DateRangeFilter
          from={ledger.from}
          to={ledger.to}
          onFromChange={(v) => setLedger("from", v)}
          onToChange={(v) => setLedger("to", v)}
        />
        <Select value={ledger.size} onChange={(e) => setLedger("size", Number(e.target.value))}>
          {[20, 50, 100, 200].map((n) => (
            <option key={n} value={n}>
              {n} / page
            </option>
          ))}
        </Select>
        <Select
          value={ledger.sortDir}
          onChange={(e) => setLedger("sortDir", e.target.value)}
          title="Sort by date"
        >
          <option value="desc">Newest first</option>
          <option value="asc">Oldest first</option>
        </Select>
        <Button
          onClick={() => {
            setLedger("from", "");
            setLedger("to", "");
            setLedger("sortDir", "desc");
          }}
        >
          Reset
        </Button>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
            {start}–{end} of {cbFmtNum(ledger.totalElements)}
          </span>
          <Button
            size="sm"
            disabled={ledger.page <= 0 || ledger.loading}
            onClick={() => setLedger("page", Math.max(0, ledger.page - 1))}
          >
            ← Prev
          </Button>
          <Button
            size="sm"
            disabled={ledger.page + 1 >= ledger.totalPages || ledger.loading}
            onClick={() => setLedger("page", ledger.page + 1)}
          >
            Next →
          </Button>
        </div>
      </ToolbarBar>

      {ledger.error ? (
        <ErrorState message={ledger.error} onRetry={() => setLedger("page", ledger.page)} />
      ) : ledger.loading && movements.length === 0 ? (
        <LoadingState label="Loading movements…" />
      ) : (
        <Table
          columns={[
            { label: "Time" },
            { label: "Type" },
            { label: "Batch" },
            { label: "Medicine" },
            { label: "Batch" },
            { label: "Qty", align: "right" },
            { label: "Balance", align: "right" },
            { label: "Ref" },
            { label: "Note" },
          ]}
        >
          {visible.length === 0 && (
            <tr>
              <Td colSpan={9} style={{ textAlign: "center", color: "var(--fg-muted)", height: 80 }}>
                {refIdFilter
                  ? `No movements for GRN #${refIdFilter} on this page.`
                  : ledger.from || ledger.to
                  ? "No movements in this date range."
                  : "No movements found."}
              </Td>
            </tr>
          )}
          {visible.map((m) => (
            <Tr key={m.id}>
              <Td mono>{fmtLedgerTime(m.ts)}</Td>
              <Td>
                <Pill
                  variant={
                    m.type === "IN"
                      ? "green"
                      : m.type === "OUT"
                      ? "neutral"
                      : m.type === "EXPIRED"
                      ? "red"
                      : "neutral"
                  }
                >
                  {m.type}
                </Pill>
              </Td>
              <Td mono>{m.lot}</Td>
              <Td style={{ fontSize: 11 }}>{m.medicineName || m.medId}</Td>
              <Td mono>{m.batch}</Td>
              <Td
                mono
                right
                style={{ fontWeight: 600, color: m.qty > 0 ? "var(--green-700)" : "var(--red-700)" }}
              >
                {m.qty > 0 ? "+" : ""}
                {cbFmtNum(m.qty)}
              </Td>
              <Td mono right>
                {cbFmtNum(m.balanceAfter)}
              </Td>
              <Td mono style={{ fontSize: 11 }}>
                {m.refType ? `${m.refType} #${m.refId}` : m.ref}
              </Td>
              <Td style={{ fontSize: 11, color: "var(--fg-muted)" }}>{m.note}</Td>
            </Tr>
          ))}
        </Table>
      )}
    </div>
  );
};

export default Inventory;
